import React, { useMemo } from "react";
import { formatDeliveryChargesSummaryDisplay } from "./OrderSummaryPriceMetaRows";
import { fmtOrderRupee } from "../pages/storeSalesDashboardHelpers";

const toNumberOrNull = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatCurrency = (value) => {
  const n = toNumberOrNull(value);
  if (n == null) return "—";
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

function SummaryRow({ label, value, bold = false, className = "" }) {
  return (
    <div className={`d-flex justify-content-between py-1 ${className}`.trim()}>
      <span className={bold ? "fw-semibold" : "text-muted"}>{label}</span>
      <span className={bold ? "fw-bold" : undefined}>{value}</span>
    </div>
  );
}

export default function DealerOrderReviewSummaryBody({
  summaryData,
  buyerPayable,
  dealerPayable,
  margin,
  creditAdjustmentsTotal = 0,
}) {
  const buyerMeta = useMemo(
    () => summaryData?.data?.price_meta ?? null,
    [summaryData]
  );
  const meta = useMemo(
    () => summaryData?.data?.dealer_price_meta ?? null,
    [summaryData]
  );

  const resolvedBuyerPayable =
    toNumberOrNull(buyerPayable) ??
    toNumberOrNull(buyerMeta?.total);
  const resolvedDealerPayable =
    toNumberOrNull(dealerPayable) ?? toNumberOrNull(meta?.total);
  const resolvedMargin =
    toNumberOrNull(margin) ??
    (() => {
      const orderPreTax =
        toNumberOrNull(buyerMeta?.base_value) ??
        toNumberOrNull(meta?.base_value);
      const dealerItemsPreTax = toNumberOrNull(meta?.total_after_discount);
      if (orderPreTax != null && dealerItemsPreTax != null) {
        return orderPreTax - dealerItemsPreTax;
      }
      return null;
    })();

  const creditFromMeta = toNumberOrNull(meta?.used_credit_notes) ?? 0;
  const creditTotal =
    creditFromMeta > 0
      ? creditFromMeta
      : Number(creditAdjustmentsTotal) || 0;

  if (!meta && resolvedBuyerPayable == null && resolvedDealerPayable == null) {
    return <p className="text-muted mb-0 small">No summary available.</p>;
  }

  const discounts = Array.isArray(meta?.discounts) ? meta.discounts : [];
  const baseDiscount = discounts.find((d) => d?.type === "base");
  const onboardingDiscount = discounts.find((d) => d?.type === "onboarding");

  const baseValue = toNumberOrNull(meta?.base_value);
  const baseEffectiveValue = toNumberOrNull(baseDiscount?.effective_value);
  const baseDiscountValue = toNumberOrNull(baseDiscount?.discount_value);
  const baseDiscountPercentage = toNumberOrNull(baseDiscount?.percentage);
  const baseDiscountSourceValue = toNumberOrNull(baseDiscount?.value);

  const onboardingEffectiveValue = toNumberOrNull(
    onboardingDiscount?.effective_value
  );
  const onboardingDiscountValue = toNumberOrNull(
    onboardingDiscount?.discount_value
  );
  const onboardingDiscountPercentage = toNumberOrNull(
    onboardingDiscount?.percentage
  );
  const onboardingDiscountSourceValue = toNumberOrNull(
    onboardingDiscount?.value
  );

  const totalAfterDiscount = toNumberOrNull(meta?.total_after_discount);
  const creditNotes = toNumberOrNull(meta?.used_credit_notes) ?? 0;
  const taxableValue = toNumberOrNull(meta?.total_after_credit_notes);
  const gst = toNumberOrNull(meta?.gst);
  const totalAfterGst = toNumberOrNull(meta?.total_after_gst);
  const deliveryCharge = toNumberOrNull(meta?.delivery_charge) ?? 0;
  const deliveryGst = toNumberOrNull(meta?.delivery_gst) ?? 0;
  const deliveryTotal = deliveryCharge + deliveryGst;
  const payable = toNumberOrNull(meta?.total);

  return (
    <div className="small">
      <SummaryRow
        label="Buyer Payable"
        value={formatCurrency(resolvedBuyerPayable)}
        bold
        className="border-bottom pb-2 mb-2"
      />
      <div className="mb-2">
        <SummaryRow
          label="My Payable"
          value={formatCurrency(resolvedDealerPayable ?? payable)}
          bold
        />
        {resolvedMargin != null && resolvedMargin !== 0 ? (
          <div className="text-muted ps-1">
            (margin: {formatCurrency(resolvedMargin)})
          </div>
        ) : null}
      </div>

      {meta ? (
        <div className="border-top pt-2 mt-2">
          <SummaryRow label="Item Total" value={formatCurrency(baseValue)} />
          {baseDiscount ? (
            <>
              <SummaryRow
                label="Base discount"
                value={`-${formatCurrency(
                  baseDiscountValue
                )} (${baseDiscountPercentage}% of ${formatCurrency(
                  baseDiscountSourceValue
                )})`}
              />
              <SummaryRow
                label="Sub-total"
                value={formatCurrency(baseEffectiveValue)}
              />
            </>
          ) : null}
          {onboardingDiscount ? (
            <>
              <SummaryRow
                label="Onboarding discount"
                value={`-${formatCurrency(
                  onboardingDiscountValue
                )} (${onboardingDiscountPercentage}% of ${formatCurrency(
                  onboardingDiscountSourceValue
                )})`}
              />
              <SummaryRow
                label="Sub-total"
                value={formatCurrency(totalAfterDiscount)}
              />
            </>
          ) : null}
          <SummaryRow
            label="Credit Notes"
            value={`-${formatCurrency(creditNotes)} (excl. GST)`}
          />
          <SummaryRow
            label="Taxable Value"
            value={formatCurrency(taxableValue)}
          />
          <SummaryRow label="GST" value={formatCurrency(gst)} />
          <SummaryRow
            label="Total (incl. GST)"
            value={formatCurrency(totalAfterGst)}
            bold
            className="border-top mt-1 pt-2"
          />
          <SummaryRow
            label="Delivery Charges"
            value={formatDeliveryChargesSummaryDisplay(
              deliveryCharge,
              deliveryGst,
              deliveryTotal
            )}
          />
          <SummaryRow
            label="Payable"
            value={formatCurrency(payable)}
            bold
            className="border-top mt-1 pt-2"
          />
        </div>
      ) : null}

      {creditTotal > 0 ? (
        <p className="text-muted mb-0 mt-3 small">
          Credits note of {fmtOrderRupee(creditTotal)} will be adjusted when you
          make the payment
        </p>
      ) : null}
    </div>
  );
}
